<template>
    <div class="revisions-list">
        <div class="revisions-header mb-3">
            <h5>{{ $t('revisionHistory') }}</h5>
            <p class="text-muted small">{{ $t('revisionHistoryDescription') }}</p>
        </div>

        <div v-if="loading" class="text-center py-5">
            <b-spinner variant="primary"></b-spinner>
            <p class="mt-2 text-muted">{{ $t('loadingRevisions') }}</p>
        </div>

        <div v-else-if="error" class="alert alert-danger">
            {{ error }}
        </div>

        <div v-else-if="revisions.length === 0" class="alert alert-info">
            {{ $t('noRevisionsFound') }}
        </div>

        <div v-else>
            <div class="revision-compare-mode mb-3" v-if="revisions.length > 1">
                <b-form-checkbox v-model="compareMode" switch size="lg">
                    {{ $t('compareMode') }}
                </b-form-checkbox>
                <small class="text-muted d-block mt-1" v-if="compareMode">
                    {{ $t('selectTwoRevisions') }}
                </small>
            </div>

            <div class="revisions-timeline">
                <div 
                    v-for="revision in sortedRevisions" 
                    :key="revision.__version__"
                    class="revision-item"
                    :class="{
                        'selected': isSelected(revision),
                        'current': isCurrentVersion(revision),
                        'clickable': compareMode
                    }"
                    @click="handleRevisionClick(revision)"
                >
                    <div class="revision-marker">
                        <div class="revision-dot" :class="getRevisionDotClass(revision)">
                            <b-icon 
                                v-if="isCurrentVersion(revision)" 
                                icon="check-circle-fill" 
                                variant="success"
                            />
                            <span v-else>{{ revision.__version__ }}</span>
                        </div>
                        <div class="revision-line" v-if="!isLastRevision(revision)"></div>
                    </div>

                    <div class="revision-content">
                        <div class="revision-meta">
                            <h6 class="mb-1">
                                <b-badge variant="primary" class="mr-2">
                                    v{{ revision.__version__ }}
                                </b-badge>
                                <span v-if="isCurrentVersion(revision)" class="text-success">
                                    <b-icon icon="star-fill" class="mr-1" />
                                    {{ $t('currentVersion') }}
                                </span>
                            </h6>
                            <small class="text-muted">
                                <b-icon icon="clock" class="mr-1" />
                                {{ $toUserTimeZone(revision.updated_at) }}
                            </small>
                            <small class="text-muted ml-2" v-if="revision.updated_by">
                                <b-icon icon="person" class="mr-1" />
                                {{ revision.updated_by }}
                            </small>
                        </div>

                        <div class="revision-actions mt-2" v-if="!compareMode">
                            <b-button 
                                size="sm" 
                                variant="outline-primary"
                                @click.stop="viewRevision(revision)"
                            >
                                <b-icon icon="eye" class="mr-1" />
                                {{ $t('view') }}
                            </b-button>
                            <b-button 
                                v-if="!isCurrentVersion(revision)"
                                size="sm" 
                                variant="outline-info"
                                class="ml-2"
                                @click.stop="compareWithCurrent(revision)"
                            >
                                <b-icon icon="arrow-left-right" class="mr-1" />
                                {{ $t('compareWithCurrent') }}
                            </b-button>
                            <b-button 
                                v-if="!isCurrentVersion(revision) && canRestore"
                                size="sm" 
                                variant="outline-warning"
                                class="ml-2"
                                @click.stop="restoreRevision(revision)"
                            >
                                <b-icon icon="arrow-counterclockwise" class="mr-1" />
                                {{ $t('restore') }}
                            </b-button>
                        </div>

                        <div v-if="compareMode && isSelected(revision)" class="mt-2">
                            <b-badge variant="info">
                                {{ $t('selectedForComparison') }}
                            </b-badge>
                        </div>
                    </div>
                </div>
            </div>

            <div class="compare-actions-sticky" v-if="compareMode && selectedRevisions.length === 2">
                <b-button 
                    variant="primary" 
                    block
                    size="lg"
                    @click="compareSelected"
                >
                    <b-icon icon="arrow-left-right" class="mr-2" />
                    {{ $t('compareRevisions', { 
                        v1: selectedRevisions[0].__version__, 
                        v2: selectedRevisions[1].__version__ 
                    }) }}
                </b-button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'RevisionsList',
    props: {
        componentUuid: {
            type: String,
            required: true
        },
        modelUuid: {
            type: String,
            required: true
        },
        currentVersion: {
            type: Number,
            default: null
        },
        currentModel: {
            type: Object,
            default: null
        },
        canRestore: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            revisions: [],
            loading: false,
            error: null,
            compareMode: false,
            selectedRevisions: []
        }
    },
    computed: {
        sortedRevisions() {
            return [...this.revisions].sort((a, b) => b.__version__ - a.__version__)
        }
    },
    methods: {
        async loadRevisions() {
            this.loading = true
            this.error = null

            try {
                const response = await this.$component(this.componentUuid)
                    .store()
                    .getRevisions(this.modelUuid)
                
                this.revisions = response.data || []
            } catch (err) {
                this.error = (err.response && err.response.data && err.response.data.message) || this.$t('errorLoadingRevisions')
                this.$debugLog(err)
            } finally {
                this.loading = false
            }
        },

        isCurrentVersion(revision) {
            return this.currentVersion && revision.__version__ === this.currentVersion
        },

        isLastRevision(revision) {
            return revision.__version__ === this.sortedRevisions[this.sortedRevisions.length - 1].__version__
        },

        getRevisionDotClass(revision) {
            if (this.isCurrentVersion(revision)) return 'current'
            if (this.isSelected(revision)) return 'selected'
            return ''
        },

        handleRevisionClick(revision) {
            if (!this.compareMode) return

            const index = this.selectedRevisions.findIndex(r => r.__version__ === revision.__version__)
            
            if (index > -1) {
                this.selectedRevisions.splice(index, 1)
            } else {
                if (this.selectedRevisions.length < 2) {
                    this.selectedRevisions.push(revision)
                } else {
                    this.selectedRevisions = [this.selectedRevisions[1], revision]
                }
            }
        },

        isSelected(revision) {
            return this.selectedRevisions.some(r => r.__version__ === revision.__version__)
        },

        viewRevision(revision) {
            this.$emit('view', revision)
        },

        restoreRevision(revision) {
            this.$mainDialog.msgBoxConfirm(
                this.$t('confirmRestoreRevision', { version: revision.__version__ }),
                {
                    title: this.$t('restoreRevision'),
                    okVariant: 'warning',
                    okTitle: this.$t('restore'),
                    cancelTitle: this.$t('cancel')
                }
            ).then(value => {
                if (value) {
                    this.$emit('restore', revision)
                }
            })
        },

        compareSelected() {
            if (this.selectedRevisions.length !== 2) return

            const [rev1, rev2] = this.selectedRevisions.sort((a, b) => a.__version__ - b.__version__)
            
            this.$emit('compare', {
                left: rev1,
                right: rev2
            })
        },

        compareWithCurrent(revision) {
            if (!this.currentModel) return

            this.$emit('compare', {
                left: revision,
                right: this.currentModel
            })
        }
    },
    watch: {
        compareMode(newVal) {
            if (!newVal) {
                this.selectedRevisions = []
            }
        }
    },
    mounted() {
        this.loadRevisions()
    }
}
</script>

<style lang="scss" scoped>
.revisions-list {
    padding: 1rem;
}

.revisions-timeline {
    position: relative;
}

.revision-item {
    display: flex;
    padding: 1rem;
    border-radius: 8px;
    transition: all 0.2s ease;
    margin-bottom: 1rem;
    background-color: var(--bg-card);
    border: 2px solid transparent;

    &.clickable {
        cursor: pointer;
        
        &:hover {
            background-color: var(--bg-hover);
            border-color: var(--border-hover);
        }
    }

    &.selected {
        border-color: var(--info);
        background-color: rgba(23, 162, 184, 0.05);
    }

    &.current {
        border-color: var(--success);
        background-color: rgba(40, 167, 69, 0.05);
    }
}

.revision-marker {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-right: 1rem;
}

.revision-dot {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: var(--bg-secondary);
    border: 3px solid var(--border-default);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 0.85rem;
    color: var(--text-primary);
    z-index: 1;
    transition: all 0.2s ease;

    &.current {
        background-color: var(--success);
        border-color: var(--success);
        color: white;
        box-shadow: 0 0 0 4px rgba(40, 167, 69, 0.2);
    }

    &.selected {
        background-color: var(--info);
        border-color: var(--info);
        color: white;
        box-shadow: 0 0 0 4px rgba(23, 162, 184, 0.2);
    }
}

.revision-line {
    width: 3px;
    flex: 1;
    background-color: var(--border-default);
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    min-height: 20px;
}

.revision-content {
    flex: 1;
}

.revision-meta {
    h6 {
        font-size: 1rem;
        font-weight: 600;
        color: var(--text-primary);
    }

    small {
        display: inline-block;
        margin-right: 0.5rem;
    }
}

.revision-compare-mode {
    padding: 1rem;
    background-color: var(--bg-secondary);
    border-radius: 8px;
    border: 1px solid var(--border-default);
}

.compare-actions-sticky {
    position: sticky;
    bottom: 0;
    background-color: var(--bg-card);
    padding: 1rem;
    margin: 0 -1rem -1rem -1rem;
    border-top: 2px solid var(--border-default);
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
    z-index: 10;
}
</style>
